import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { compareVersions, linesBetween } from "./changelog";
import { MAX_RANGE_LINES } from "./supportBriefings";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fields: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(2),
      marginTop: theme.spacing(2)
    },
    help: {
      fontSize: 13.5,
      color: theme.palette.text.secondary
    },
    error: {
      fontSize: 13,
      color: theme.palette.error.main,
      marginTop: theme.spacing(1.5)
    }
  })
);

interface SupportRangeDialogProps {
  open: boolean;
  /** Every version in the CHANGELOG, newest first */
  versions: string[];
  /** Version selected in Novidades, used as the initial interval */
  initial: string;
  onClose: () => void;
  /** Called with the interval in ascending order */
  onConfirm: (from: string, to: string) => void;
}

/** Picks an interval of versions, at most MAX_RANGE_LINES version lines wide, for the support summary */
const SupportRangeDialog: React.FC<SupportRangeDialogProps> = ({ open, versions, initial, onClose, onConfirm }) => {
  const classes = useStyles();
  const [from, setFrom] = useState(initial);
  const [to, setTo] = useState(initial);

  useEffect(() => {
    if (open) {
      setFrom(initial);
      setTo(initial);
    }
  }, [open, initial]);

  const [low, high] = compareVersions(from, to) <= 0 ? [from, to] : [to, from];
  const lines = linesBetween(versions, low, high);
  const tooWide = lines.length > MAX_RANGE_LINES;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="support-range-title">
      <DialogTitle id="support-range-title">Resumo de um intervalo</DialogTitle>
      <DialogContent>
        <Typography className={classes.help}>
          {`Escolha até ${MAX_RANGE_LINES} linhas de versão, como 1.106.0 → 1.107.13. O resumo junta destaques, melhorias e correções.`}
        </Typography>
        <div className={classes.fields}>
          <TextField
            select
            label="De"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            SelectProps={{ native: true }}
            inputProps={{ "aria-label": "De" }}
            variant="outlined"
            size="small"
          >
            {versions.map((version) => <option key={version} value={version}>{version}</option>)}
          </TextField>
          <TextField
            select
            label="Até"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            SelectProps={{ native: true }}
            inputProps={{ "aria-label": "Até" }}
            variant="outlined"
            size="small"
          >
            {versions.map((version) => <option key={version} value={version}>{version}</option>)}
          </TextField>
        </div>
        {tooWide && (
          <Typography className={classes.error} role="alert">
            {`O intervalo passa por ${lines.length} linhas (${lines.join(", ")}). Escolha até ${MAX_RANGE_LINES}.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={tooWide} onClick={() => onConfirm(low, high)}>Abrir resumo</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportRangeDialog;
